import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { UserFullname } from '@r/components/user-fullname.tsx';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import { Badge } from '@r/components/ui/badge.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { cn } from '@r/utils/utils.ts';
import { PencilIcon } from 'lucide-react';
import type { DataTableFilterField } from '@r/types/index.ts';
import {
  WaitingListWaitingListReadModelStatus,
  type WaitingListWaitingListReadModel
} from '@r/api/generated/client.schemas.ts';

export type WaitingListTableRow = WaitingListWaitingListReadModel;

export const useWaitingListFilterFields = (): DataTableFilterField<WaitingListTableRow>[] => {
  const { t } = useTranslation();

  const statusOptions = Object.values(WaitingListWaitingListReadModelStatus).map((status) => ({
    label: t(status),
    value: status
  }));

  return [
    {
      label: t('label.user.lastname'),
      value: 'lastname'
    },
    {
      label: t('label.user.firstname'),
      value: 'firstname'
    },
    {
      label: t('label.user.email'),
      value: 'email'
    },
    {
      label: t('label.company.name'),
      value: 'companyName'
    },
    {
      label: t('label.waiting.list.created.by'),
      value: 'createdBy',
      placeholder: t('label.user.unique.code')
    },
    {
      label: t('label.waiting.list.request.date'),
      value: 'createdAt',
      isDateRange: true
    },
    {
      label: t('label.waiting.list.status'),
      value: 'status' as keyof WaitingListTableRow,
      options: statusOptions
    }
  ];
};

function WaitingListStatusBadge({ status }: { status: WaitingListWaitingListReadModel['status'] }) {
  const { t } = useTranslation();

  const { dotClass, label } = (() => {
    switch (status) {
      case WaitingListWaitingListReadModelStatus.waitingliststatuspending:
        return { dotClass: 'bg-blue-500', label: t(status) };
      case WaitingListWaitingListReadModelStatus.waitingliststatusapproved:
        return { dotClass: 'bg-emerald-600', label: t(status) };
      case WaitingListWaitingListReadModelStatus.waitingliststatusrejected:
        return { dotClass: 'bg-muted-foreground', label: t(status) };
      default:
        return { dotClass: 'bg-muted-foreground', label: status };
    }
  })();

  return (
    <Badge variant="outline" className="gap-1.5 font-normal border-muted-foreground/20">
      <span className={cn('size-1.5 shrink-0 rounded-full', dotClass)} aria-hidden />
      {label}
    </Badge>
  );
}

export function isWaitingListRowEditable(row: WaitingListTableRow): boolean {
  return row.status === WaitingListWaitingListReadModelStatus.waitingliststatuspending;
}

export function useWaitingListColumns({
  onEdit,
  canEdit
}: {
  onEdit: (row: WaitingListTableRow) => void;
  canEdit: boolean;
}): ColumnDef<WaitingListTableRow>[] {
  const { t } = useTranslation();

  const columns: ColumnDef<WaitingListTableRow>[] = [
    {
      accessorKey: 'lastname',
      header: t('label.user.lastname')
    },
    {
      accessorKey: 'firstname',
      header: t('label.user.firstname')
    },
    {
      accessorKey: 'email',
      header: t('label.user.email')
    },
    {
      accessorKey: 'companyName',
      header: t('label.company.name')
    },
    {
      id: 'createdBy',
      accessorFn: (row) => row.createdBy?.user?.fullname ?? '',
      header: t('label.waiting.list.created.by'),
      cell: ({ row }: CellContext<WaitingListTableRow, unknown>) => (
        <UserFullname
          fullname={row.original.createdBy?.user?.fullname ?? ''}
          isDeleted={row.original.createdBy?.user?.isDeleted}
        />
      )
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.waiting.list.request.date')} />
      ),
      cell: ({ row }) =>
        row.original.createdAt
          ? format(new Date(row.original.createdAt), 'dd/MM/yyyy', { locale: fr })
          : ''
    },
    {
      accessorKey: 'status',
      header: t('label.waiting.list.status'),
      cell: ({ row }) => <WaitingListStatusBadge status={row.original.status} />
    }
  ];

  if (canEdit) {
    columns.push({
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => (
        <div className="flex justify-end items-center">
          {isWaitingListRowEditable(row.original) ? (
            <Button
              type="button"
              variant="raw"
              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground h-fit"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(row.original);
              }}
              title={t('common.edit')}
            >
              <PencilIcon size={18} />
            </Button>
          ) : null}
        </div>
      )
    });
  }

  return columns;
}
